import React, { useState, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCollection, addDocument, updateDocument, deleteDocument } from '@/hooks/useFirestore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  Search, 
  ListVideo,
  Edit,
  Trash2,
  GripVertical,
  Image as ImageIcon,
  Video,
  Monitor,
  Clock,
  Play
} from 'lucide-react';

const SortableMediaItem = ({ media, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: media.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-2 bg-slate-900/50 rounded-lg border border-slate-700"
    >
      <div {...attributes} {...listeners} className="cursor-grab">
        <GripVertical className="w-4 h-4 text-slate-500" />
      </div>
      <div className="w-16 h-10 rounded overflow-hidden bg-slate-800 flex-shrink-0">
        {media.type === 'image' ? (
          <img src={media.url} alt={media.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Video className="w-4 h-4 text-slate-500" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{media.name}</p>
        <p className="text-xs text-slate-500">{media.type}</p>
      </div>
      <Button variant="ghost" size="sm" onClick={() => onRemove(media.id)} className="text-red-400 hover:text-red-300">
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
};

const PlaylistForm = ({ playlist, allMedia, screens, onSave, onClose }) => {
  const [formData, setFormData] = useState(playlist || {
    name: '',
    description: '',
    mediaIds: [],
    duration: 10,
    assignedScreens: []
  });
  const [loading, setLoading] = useState(false);
  const [addMediaOpen, setAddMediaOpen] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const selectedMedia = formData.mediaIds
    .map(id => allMedia.find(m => m.id === id))
    .filter(Boolean);

  const availableMedia = allMedia.filter(m => !formData.mediaIds.includes(m.id));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = formData.mediaIds.indexOf(active.id);
      const newIndex = formData.mediaIds.indexOf(over.id);
      setFormData({
        ...formData,
        mediaIds: arrayMove(formData.mediaIds, oldIndex, newIndex)
      });
    }
  };

  const addMedia = (mediaId) => {
    setFormData({
      ...formData,
      mediaIds: [...formData.mediaIds, mediaId]
    });
  };

  const removeMedia = (mediaId) => {
    setFormData({
      ...formData,
      mediaIds: formData.mediaIds.filter(id => id !== mediaId)
    });
  };

  const toggleScreen = (screenId) => {
    const current = formData.assignedScreens || [];
    if (current.includes(screenId)) {
      setFormData({ ...formData, assignedScreens: current.filter(id => id !== screenId) });
    } else {
      setFormData({ ...formData, assignedScreens: [...current, screenId] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="playlistName">Playlist Name *</Label>
          <Input
            id="playlistName"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Main Menu Playlist"
            required
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">Duration per item (seconds)</Label>
          <Input
            id="duration"
            type="number"
            min="1"
            max="300"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Media Items ({selectedMedia.length})</Label>
        <div className="border border-slate-700 rounded-lg p-3 min-h-[120px]">
          {selectedMedia.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">No media added. Click "Add Media" to add items.</p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={formData.mediaIds} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {selectedMedia.map((media) => (
                    <SortableMediaItem key={media.id} media={media} onRemove={removeMedia} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => setAddMediaOpen(true)}>
          <Plus className="w-4 h-4 mr-1" />
          Add Media
        </Button>
      </div>

      <div className="space-y-2">
        <Label>Assign to Screens</Label>
        <ScrollArea className="h-32 border border-slate-700 rounded-lg p-3">
          <div className="space-y-2">
            {screens.map(screen => (
              <div key={screen.id} className="flex items-center gap-2">
                <Checkbox
                  id={`screen-${screen.id}`}
                  checked={(formData.assignedScreens || []).includes(screen.id)}
                  onCheckedChange={() => toggleScreen(screen.id)}
                />
                <label htmlFor={`screen-${screen.id}`} className="text-sm text-slate-300 cursor-pointer">
                  {screen.name}
                </label>
              </div>
            ))}
          </div>
        </ScrollArea>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Saving...' : (playlist ? 'Update' : 'Create')} Playlist
        </Button>
      </DialogFooter>

      {/* Add Media Dialog */}
      <Dialog open={addMediaOpen} onOpenChange={setAddMediaOpen}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle>Add Media to Playlist</DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-80">
            <div className="grid grid-cols-3 gap-2">
              {availableMedia.map(media => (
                <div
                  key={media.id}
                  className="cursor-pointer rounded-lg overflow-hidden border border-slate-700 hover:border-indigo-500 transition-colors"
                  onClick={() => { addMedia(media.id); setAddMediaOpen(false); }}
                >
                  <div className="aspect-video bg-slate-900">
                    {media.type === 'image' ? (
                      <img src={media.url} alt={media.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-6 h-6 text-slate-500" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-white p-1 truncate">{media.name}</p>
                </div>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </form>
  );
};

const Playlists = () => {
  const { isHQ } = useAuth();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);

  const { documents: playlists, loading } = useCollection('playlists');
  const { documents: media } = useCollection('media');
  const { documents: screens } = useCollection('screens');

  const filteredPlaylists = useMemo(() => {
    return playlists.filter(p => 
      p.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [playlists, searchTerm]);

  const handleSavePlaylist = async (data) => {
    if (editingPlaylist) {
      await updateDocument('playlists', editingPlaylist.id, data);
      // Update screens with this playlist
      for (const screenId of data.assignedScreens || []) {
        await updateDocument('screens', screenId, { playlistId: editingPlaylist.id });
      }
      toast({ title: 'Playlist updated successfully' });
    } else {
      const playlistId = await addDocument('playlists', data);
      // Assign to screens
      for (const screenId of data.assignedScreens || []) {
        await updateDocument('screens', screenId, { playlistId });
      }
      toast({ title: 'Playlist created successfully' });
    }
    setEditingPlaylist(null);
  };

  const handleDeletePlaylist = async (playlistId) => {
    await deleteDocument('playlists', playlistId);
    toast({ title: 'Playlist deleted successfully' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Playlists</h1>
          <p className="text-slate-400">Create and manage media playlists for your screens</p>
        </div>
        {isHQ && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => setEditingPlaylist(null)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Playlist
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editingPlaylist ? 'Edit' : 'Create'} Playlist</DialogTitle>
                <DialogDescription>Configure your media playlist</DialogDescription>
              </DialogHeader>
              <PlaylistForm 
                playlist={editingPlaylist} 
                allMedia={media}
                screens={screens}
                onSave={handleSavePlaylist} 
                onClose={() => { setDialogOpen(false); setEditingPlaylist(null); }}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search playlists..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-slate-800/50 border-slate-700"
        />
      </div>

      {/* Playlists Grid */}
      {filteredPlaylists.length === 0 ? (
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ListVideo className="w-12 h-12 text-slate-500 mb-4" />
            <p className="text-slate-400">No playlists found</p>
            {isHQ && <p className="text-sm text-slate-500">Click "Create Playlist" to get started</p>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlaylists.map(playlist => {
            const playlistMedia = (playlist.mediaIds || []).map(id => media.find(m => m.id === id)).filter(Boolean);
            const assignedScreenCount = screens.filter(s => s.playlistId === playlist.id).length;
            const totalDuration = playlistMedia.length * (playlist.duration || 10);

            return (
              <Card key={playlist.id} className="bg-slate-800/50 border-slate-700">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-white">{playlist.name}</CardTitle>
                      <CardDescription>{playlist.description || 'No description'}</CardDescription>
                    </div>
                    <Badge variant="secondary">
                      {playlistMedia.length} items
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Media Preview */}
                  <div className="flex gap-1 mb-4">
                    {playlistMedia.slice(0, 5).map((m, i) => (
                      <div key={i} className="w-12 h-8 bg-slate-900 rounded overflow-hidden">
                        {m.type === 'image' ? (
                          <img src={m.url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Video className="w-4 h-4 text-slate-500" />
                          </div>
                        )}
                      </div>
                    ))}
                    {playlistMedia.length > 5 && (
                      <div className="w-12 h-8 bg-slate-900 rounded flex items-center justify-center text-xs text-slate-400">
                        +{playlistMedia.length - 5}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4 text-sm text-slate-400 mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{Math.floor(totalDuration / 60)}m {totalDuration % 60}s</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Monitor className="w-4 h-4" />
                      <span>{assignedScreenCount} screens</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditingPlaylist(playlist); setDialogOpen(true); }}>
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                    {isHQ && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" className="text-red-400 hover:text-red-300">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-slate-800 border-slate-700">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Playlist</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{playlist.name}"? This will not delete the media files.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDeletePlaylist(playlist.id)} className="bg-red-600 hover:bg-red-700">
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Playlists;
